import React, {useEffect, useState} from "react";
import {observer} from "mobx-react-lite";
import EventCatalogueTemplate from "../../../components/templates/eventCatalogue/eventCatalogueTemplate";
import {useStores} from "../../../stores/useStores";
import {EventCatalogueSortField} from "../../../shared/enums/eventCatalogueSortField";
import EventTemplateSummaryItem from "../../../stores/domainStore/pages/models/eventTemplateSummaryItem";
import {EventTemplateStatus} from "../../../shared/enums/eventTemplateStatus";
import {useTranslation} from "react-i18next";
import {ToastMessageStatus} from "../../../components/atoms/toastMessage/toastMessage";
import EventTemplateDetailModal from "../../../components/organisms/eventTemplateDetailModal/eventTemplateDetailModal";
import {EventTemplateDetail} from "../../../stores/domainStore/pages/models/eventTemplateDetail";
import {runInAction} from "mobx";
import {eventCalendarTypeFromCatalogueParam} from "../../../shared/eventCalendarTypeHelper";
import {useHistory} from "react-router";
import {Navigation} from "../../../utils/navigation";
import {QueryParams} from "../../../shared/enums/queryParams";

export interface IDisplayEventCatalogueProps {
  switchToAddNewTemplate: () => void;
  switchToEditTemplate: (eventTemplateId: string) => void;
}

class CatalogueState {
  eventTemplateStatus: EventTemplateStatus;
  currentCategory: string | null;

  constructor(
    eventTemplateStatus: EventTemplateStatus,
    currentCategory: string | null
  ) {
    this.eventTemplateStatus = eventTemplateStatus;
    this.currentCategory = currentCategory;
  }
}
const DisplayEventCatalogue: React.FC<IDisplayEventCatalogueProps> = ({
  switchToAddNewTemplate,
  switchToEditTemplate
}) => {
  const maxTemplatesAllowed = 9999;
  const {
    domainStore: {eventCatalogueDomainStore},
    uiStore: {eventCatalogueUiStore, toastMessageStore}
  } = useStores();
  const [catalogueState, setCatalogueState] = useState<CatalogueState>(
    new CatalogueState(EventTemplateStatus.Active, null)
  );

  const {t} = useTranslation(["eventTemplate"]);

  const history = useHistory();

  const catalogueType = eventCalendarTypeFromCatalogueParam(
    Navigation.getQueryParamByKey(history, QueryParams.type)
  );

  useEffect(() => {
    const controller = new AbortController();
    const refreshTemplates = async () => {
      eventCatalogueUiStore.resetEventTemplateCategories();
      await eventCatalogueUiStore.loadEventCatalogue(
        catalogueState.eventTemplateStatus,
        maxTemplatesAllowed,
        EventCatalogueSortField.title,
        undefined,
        undefined,
        catalogueType,
        controller
      );
      if (controller.signal.aborted) {
        return;
      }
      if (catalogueState.currentCategory) {
        runInAction(() => {
          eventCatalogueUiStore.expandedCategoryId =
            catalogueState.currentCategory!;
        });
      }
    };
    refreshTemplates();
    return () => controller.abort();
  }, [catalogueState, eventCatalogueUiStore, catalogueType]);

  const updateTemplateStatus = async (
    template: EventTemplateSummaryItem,
    status: EventTemplateStatus
  ) => {
    let updatedStatusDescription =
      status === EventTemplateStatus.Active
        ? t("eventTemplate:messages.unarchived")
        : t("eventTemplate:messages.archived");
    try {
      await eventCatalogueDomainStore.setEventTemplateStatus(
        template.id,
        status
      );
      toastMessageStore.displayToastMessage(
        t("eventTemplate:messages.templateStatusUpdate", {
          eventTemplate: template.title,
          status: updatedStatusDescription
        }),
        ToastMessageStatus.Success
      );
    } catch {
      toastMessageStore.displayToastMessage(
        t("eventTemplate:messages.templateStatusError", {
          eventTemplate: template.title,
          status: updatedStatusDescription
        }),
        ToastMessageStatus.Danger
      );
    } finally {
      setCatalogueState(
        new CatalogueState(
          catalogueState.eventTemplateStatus,
          eventCatalogueUiStore.expandedCategoryId
        )
      );
      eventCatalogueUiStore.reloadCatalogueSearchResult();
    }
  };

  const doUpdateStatusSwitched = (templateStatus: EventTemplateStatus) => {
    setCatalogueState(new CatalogueState(templateStatus, null));
  };

  const doTemplateSelect = async (
    template: EventTemplateSummaryItem
  ): Promise<void> => {
    await eventCatalogueDomainStore.loadEventTemplate(template.id);

    runInAction(() => {
      eventCatalogueUiStore.eventTemplateDetail = new EventTemplateDetail(
        template.id,
        template.category,
        template.title,
        catalogueState.eventTemplateStatus,
        eventCatalogueDomainStore.eventTemplate.durationDescription,
        template.description,
        template.isVillageFavourite
      );
    });
  };

  return (
    <>
      <EventCatalogueTemplate
        templateViewStatus={catalogueState.eventTemplateStatus}
        eventTemplateCategories={
          eventCatalogueDomainStore.eventTemplateCategories
        }
        isLoading={eventCatalogueUiStore.isLoading}
        onAddNewTemplateClick={() => {
          eventCatalogueDomainStore.resetEventTemplate();
          switchToAddNewTemplate();
        }}
        onEditTemplate={(template) => switchToEditTemplate(template.id)}
        onSelectTemplate={doTemplateSelect}
        onUpdateTemplateStatusClick={updateTemplateStatus}
        onTemplateStatusChanged={doUpdateStatusSwitched}
      />
      {eventCatalogueUiStore.eventTemplateDetail && (
        <EventTemplateDetailModal
          templateDetail={eventCatalogueUiStore.eventTemplateDetail}
          isLoading={false}
          isOpen={true}
          onEditTemplate={(templateId) => switchToEditTemplate(templateId)}
          onClose={eventCatalogueUiStore.resetEventTemplateDetail}
        />
      )}
    </>
  );
};

export default observer(DisplayEventCatalogue);
